import React, { createContext, useContext, ReactNode, useState, useCallback } from 'react';
import { NOTIFICATION_TEXTS } from '../../presentation/utils/texts';
import { getTimeDifference } from '../../presentation/utils/dateUtils';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: Date;
}

interface NotificationContextType {
  notifications: Notification[];
  showSuccess: (message: string, title?: string) => void;
  showError: (message: string, title?: string) => void;
  showWarning: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Generate unique ID for notifications
  const generateId = useCallback(() => {
    return `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  // Add notification with auto-dismiss
  const addNotification = useCallback((type: NotificationType, message: string, title?: string) => {
    const id = generateId();
    const notification: Notification = {
      id,
      type,
      title: title || getDefaultTitle(type),
      message,
      timestamp: new Date(),
    };

    setNotifications(prev => {
      // Prevent duplicate messages
      const isDuplicate = prev.some(n => 
        n.message === message && 
        n.type === type && 
        getTimeDifference(n.timestamp) < 1000 // Within 1 second
      );
      
      if (isDuplicate) {
        return prev;
      }

      // Add to the beginning of the array (newest first)
      const newNotifications = [notification, ...prev];
      
      // Keep only the latest 5 notifications
      return newNotifications.slice(0, 5);
    });

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      removeNotification(id);
    }, 5000);
  }, [generateId, removeNotification]);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const showSuccess = useCallback((message: string, title?: string) => {
    addNotification('success', message, title);
  }, [addNotification]);

  const showError = useCallback((message: string, title?: string) => {
    addNotification('error', message, title);
  }, [addNotification]);

  const showWarning = useCallback((message: string, title?: string) => {
    addNotification('warning', message, title);
  }, [addNotification]);

  const showInfo = useCallback((message: string, title?: string) => {
    addNotification('info', message, title);
  }, [addNotification]);

  const value: NotificationContextType = {
    notifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeNotification,
    clearAllNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Helper function to get default titles
const getDefaultTitle = (type: NotificationType): string => {
  switch (type) {
    case 'success':
      return NOTIFICATION_TEXTS.defaultTitles.success;
    case 'error':
      return NOTIFICATION_TEXTS.defaultTitles.error;
    case 'warning':
      return NOTIFICATION_TEXTS.defaultTitles.warning;
    case 'info':
      return NOTIFICATION_TEXTS.defaultTitles.info;
    default:
      return NOTIFICATION_TEXTS.defaultTitles.default;
  }
};

export const useNotificationContext = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
};